import { checkMobilePhone, transformOrderId } from "util/orderUtils";

// Matches the server's limit in ob-server APIs/queue/queueUtils.js.
export const MAX_GUESTS = 99;

// People type mobile numbers with spaces, dashes or brackets.
export const normalizePhone = (phone) => String(phone || "").replace(/[\s\-()]/g, "");

export const isQueueFormValid = ({ name, guestNum, phoneNumber }) => {
  const guests = Number(guestNum);
  return (
    String(name || "").trim().length > 0 &&
    Number.isInteger(guests) &&
    guests >= 1 &&
    guests <= MAX_GUESTS &&
    checkMobilePhone(normalizePhone(phoneNumber))
  );
};

// Minutes since a millisecond timestamp, never negative.
export const minutesWaited = (createdAtMillis, now = Date.now()) =>
  createdAtMillis ? Math.max(0, Math.floor((now - createdAtMillis) / 60000)) : null;

// The short number guests are given; older entries only have the doc id.
export const queueRefLabel = ({ queueNumber, reference, id } = {}) =>
  queueNumber ? `#${queueNumber}` : transformOrderId(reference || id);

export const queueStatusPath = (restaurantId, reference) =>
  `/queue/${encodeURIComponent(restaurantId)}/status/${encodeURIComponent(reference)}`;

// What to tell the guest when their entry changes between two polls, or null.
// Calling again (a newer calledAt) notifies again.
export const queueStatusChange = (prev, next) => {
  if (!prev || !next) return null;
  if (next.status === "called" && (prev.status !== "called" || next.calledAt !== prev.calledAt)) {
    return { title: "Your table is ready", body: "Please come to the front counter now." };
  }
  if (next.status === "seated" && prev.status !== "seated") {
    return { title: "You've been seated", body: "Enjoy your meal!" };
  }
  if (next.status === "cancelled" && prev.status !== "cancelled") {
    return { title: "You've left the queue", body: "Please speak to our staff if this is a mistake." };
  }
  return null;
};

// Mirrors the server's isQueueDisabled (ob-server APIs/queue/queueUtils.js):
// the portal's QR Codes page saves { name: "queue", disabled } here.
export const isQueueClosed = (restaurant) =>
  Boolean(
    restaurant &&
      Array.isArray(restaurant.qrCodeSettings) &&
      restaurant.qrCodeSettings.some((s) => s && s.name === "queue" && s.disabled === true)
  );

// SMS sent when staff call a guest. Mirrors the server's renderQueueSms and
// limits (ob-server APIs/queue/queueUtils.js), which does the real sending.
export const MAX_SMS_TEMPLATE_LENGTH = 320;
export const SMS_PLACEHOLDERS = [
  { key: "{name}", label: "Guest name" },
  { key: "{number}", label: "Queue number" },
  { key: "{guests}", label: "Guests" },
  { key: "{restaurant}", label: "Restaurant" },
  { key: "{link}", label: "Status link" },
];
export const DEFAULT_SMS_TEMPLATE =
  "Hi {name}, your table at {restaurant} is ready ({number}). Please come to the front counter. {link}";

export const renderQueueSms = (template, values = {}) =>
  SMS_PLACEHOLDERS.reduce((text, { key }) => {
    const value = values[key.slice(1, -1)];
    return text.split(key).join(value === undefined || value === null ? "" : String(value));
  }, String(template || ""))
    .replace(/[ \t]{2,}/g, " ")
    .trim();

// How many SMS parts a message costs: 160 characters for plain text (153 per
// part once split), 70 (67) when it has characters outside GSM, such as emoji or Chinese.
export const smsParts = (text) => {
  const length = [...String(text || "")].length;
  if (length === 0) return 0;
  // eslint-disable-next-line no-control-regex
  const unicode = /[^\x00-\x7F£¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ¤¡ÄÖÑÜ§¿äöñüà€]/.test(text);
  const single = unicode ? 70 : 160;
  const multi = unicode ? 67 : 153;
  return length <= single ? 1 : Math.ceil(length / multi);
};
